/** Paywalled users: this many highest-priority findings stay readable (not blurred). */
export const DIAGNOSIS_FREE_PREVIEW_LIMIT = 2
/** Max blurred-only teaser rows after the preview when paywalled. */
export const DIAGNOSIS_MAX_BLURRED_TEASERS = 3

export const DIAGNOSIS_PAYWALL_LIST_CAP = DIAGNOSIS_FREE_PREVIEW_LIMIT + DIAGNOSIS_MAX_BLURRED_TEASERS

export const FINDING_WRAPPER_KEYS = [
  'id',
  'query_id',
  'query_text',
  'diagnosis_report_id',
  'diagnosis_status',
  'key'
]

export const GAP_CATEGORY_BY_CODE = {
  VISIBILITY_GAP: 'content & visibility',
  CONTENT_GAP: 'content & visibility',
  KEYWORD_GAP: 'content & visibility',
  BRAND_VOICE_GAP: 'content & visibility',
  PERSONA_ALIGNMENT_GAP: 'content & visibility',
  SCHEMA_GAP: 'structure',
  FAQ_GAP: 'structure',
  COMPARISON_PAGE_GAP: 'structure',
  THIRD_PARTY_COVERAGE_GAP: 'authority & trust',
  SOURCE_INFLUENCE_GAP: 'authority & trust',
  TRUST_SIGNAL_GAP: 'authority & trust',
  TECHNICAL_BLOCKER: 'technical',
  RETRIEVAL_GAP: 'technical',
  TRUST_SIGNAL_GAP_PROVIDER: 'provider-level',
  THIRD_PARTY_COVERAGE_GAP_PROVIDER: 'provider-level',
  SOURCE_INFLUENCE_GAP_PROVIDER: 'provider-level',
  EVIDENCE_INSUFFICIENT: 'utility'
}

export const GAP_BAND_META = {
  VISIBILITY_GAP: {
    title: 'Citation gap',
    badge: 'VISIBILITY-SPECIFIC',
    desc: 'How often the brand is cited versus competitors, and where citations should land.'
  },
  CONTENT_GAP: {
    title: 'Missing content',
    badge: 'CONTENT-SPECIFIC',
    desc: 'The exact intent this brand has no page for, and what competitors publish instead.'
  },
  KEYWORD_GAP: {
    title: 'Keyword coverage',
    badge: 'KEYWORD-SPECIFIC',
    desc: "Missing vs. recommended terminology — the specific words competitors use that this brand's content doesn't."
  },
  BRAND_VOICE_GAP: {
    title: 'Brand voice',
    badge: 'VOICE-SPECIFIC',
    desc: 'How AI describes this category versus the brand’s current voice.'
  },
  PERSONA_ALIGNMENT_GAP: {
    title: 'Persona alignment',
    badge: 'PERSONA-SPECIFIC',
    desc: "Whether this content's intent and language actually matches the persona behind the query."
  },
  SCHEMA_GAP: {
    title: 'Schema by page',
    badge: 'SCHEMA-SPECIFIC',
    desc: 'Every page the diagnosis examined that carries a defect — what its markup does today, and what it should do instead.'
  },
  FAQ_GAP: {
    title: 'Proposed FAQ content',
    badge: 'FAQ-SPECIFIC',
    desc: 'Questions users are asking that go unanswered.'
  },
  COMPARISON_PAGE_GAP: {
    title: 'Comparison positioning',
    badge: 'STRUCTURE-SPECIFIC',
    desc: 'The comparison query type to target and who the brand should be compared against.'
  },
  THIRD_PARTY_COVERAGE_GAP: {
    title: 'Third-party coverage map',
    badge: 'AUTHORITY-SPECIFIC',
    desc: 'Where cited sources mention the brand, ignore it, or omit it entirely.'
  },
  SOURCE_INFLUENCE_GAP: {
    title: 'Sources driving AI answer',
    badge: 'AUTHORITY-SPECIFIC',
    desc: 'Which high-authority sources shape the answer and where competitors are cited instead.'
  },
  TRUST_SIGNAL_GAP: {
    title: 'Trust signals',
    badge: 'AUTHORITY-SPECIFIC',
    desc: 'On-site and third-party trust indicators that are present versus missing.'
  },
  TECHNICAL_BLOCKER: {
    title: 'Technical diagnostics',
    badge: 'TECHNICAL-SPECIFIC',
    desc: 'The crawl or indexing blocker, severity, and affected URLs.'
  },
  RETRIEVAL_GAP: {
    title: 'What the crawler sees',
    badge: 'TECHNICAL-SPECIFIC',
    desc: 'Format and retrieval issues that keep existing content from being extracted.'
  },
  TRUST_SIGNAL_GAP_PROVIDER: {
    title: 'Provider trust signals',
    badge: 'PROVIDER-SPECIFIC',
    desc: 'Bio quality, credentials, and recognitions for this provider.'
  },
  THIRD_PARTY_COVERAGE_GAP_PROVIDER: {
    title: 'Provider directory coverage',
    badge: 'PROVIDER-SPECIFIC',
    desc: 'Directories this provider is listed on versus missing from.'
  },
  SOURCE_INFLUENCE_GAP_PROVIDER: {
    title: 'Named-provider effect',
    badge: 'PROVIDER-SPECIFIC',
    desc: 'Whether this provider is named in the answer, and which competitors are named instead.'
  },
  EVIDENCE_INSUFFICIENT: {
    title: "What's unclear",
    badge: 'UTILITY-SPECIFIC',
    desc: 'What could not be determined from the collected evidence, and what to investigate next.'
  }
}

/**
 * page_diagnoses[].verdict vocabulary. Values arrive lowercase; `label` is what the UI shows.
 * `conformant` and `not_assessed` carry no issues — only `not_assessed` is rendered, since a
 * page that could not be fetched is a blind spot worth surfacing, while a clean page is noise.
 */
export const SCHEMA_VERDICT_META = {
  absent: {
    label: 'ABSENT',
    tone: 'negative',
    desc: 'no schema markup exists on the page'
  },
  invalid: {
    label: 'INVALID',
    tone: 'warn',
    desc: 'markup is structurally broken — a property on a type that does not support it, a duplicated block, or a conflicting entity URL'
  },
  partial: {
    label: 'PARTIAL',
    tone: 'info',
    desc: 'markup exists but is incomplete or needs refinement'
  },
  conformant: {
    label: 'CONFORMANT',
    tone: 'positive',
    desc: 'fetched and clean — no schema defects were found'
  },
  not_assessed: {
    label: 'NOT ASSESSED',
    tone: 'neutral',
    desc: 'the page could not be fetched, so its markup was never judged'
  }
}

export function normalizeFindings(rawFindings) {
  if (!rawFindings) return []

  let value = rawFindings
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return []
    }
  }

  if (Array.isArray(value)) return value

  if (value && typeof value === 'object') {
    if (Array.isArray(value.findings)) return value.findings
    const objectValues = Object.values(value)
    if (objectValues.every((item) => item && typeof item === 'object')) {
      return objectValues
    }
  }

  return []
}

export function withFindingKeys(findings) {
  return (findings || []).map((finding, index) => ({
    ...finding,
    key: finding.id || `normalized-${index}`
  }))
}

export function sortFindingsByPriority(findings) {
  const severityRank = { critical: 0, high: 1, medium: 2, low: 3 }
  const effortRank = { low: 0, medium: 1, high: 2 }
  return [...findings].sort((a, b) => {
    const sevDelta = (severityRank[String(a.severity || '').toLowerCase()] ?? 99)
      - (severityRank[String(b.severity || '').toLowerCase()] ?? 99)
    if (sevDelta !== 0) return sevDelta
    return (effortRank[String(a.fix_effort || '').toLowerCase()] ?? 99)
      - (effortRank[String(b.fix_effort || '').toLowerCase()] ?? 99)
  })
}

export function issueGroupKey(item) {
  const code = String(item?.code || '').trim().toLowerCase()
  if (code) return `code:${code}`
  return `title:${String(item?.title || '').trim().toLowerCase()}`
}

export function affectedQueriesFor(item, allFindings) {
  const groupKey = issueGroupKey(item)
  const seen = new Set()
  return (allFindings || [])
    .filter((candidate) => issueGroupKey(candidate) === groupKey)
    .map((candidate) => ({
      id: candidate.query_id,
      text: candidate.query_text
    }))
    .filter((entry) => {
      const key = `${entry.id}-${entry.text}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

export function gapTypeChips(item) {
  const rootCause = (item?.root_cause_category || '').toLowerCase()
  if (rootCause === 'combination') return ['content', 'authority', 'retrieval']
  if (rootCause) return [rootCause]

  const codePrefix = String(item?.code || '')
    .toLowerCase()
    .replace(/_gap$/, '')
  return codePrefix ? [codePrefix] : ['other']
}

export function formatGapTypeLabel(value) {
  if (!value) return 'other'
  return String(value).replace(/_/g, ' ')
}

export function gapCategoryLabel(item) {
  const code = String(item?.code || '').toUpperCase()
  return GAP_CATEGORY_BY_CODE[code] || formatGapTypeLabel(gapTypeChips(item)[0])
}

export function severityColor(severity) {
  const value = (severity || '').toLowerCase()
  if (value === 'critical') return 'error'
  if (value === 'high') return 'warning'
  if (value === 'medium') return 'info'
  return 'default'
}

export function normalizeLabel(value) {
  if (!value) return 'Unknown'
  return String(value).charAt(0).toUpperCase() + String(value).slice(1).replace(/_/g, ' ')
}

function previewIdFor(finding) {
  const id = finding?.id || finding?.key
  return id == null ? null : String(id)
}

/**
 * What a paywalled plan may see: the readable findings and the blurred teasers, by id.
 * Uses the backend's diagnosis_preview; an older payload without one falls back to positions.
 */
export function paywallPreview(preview, prioritized) {
  if (preview && Array.isArray(preview.finding_ids)) {
    return {
      readableIds: preview.finding_ids.map(String),
      teaserIds: Array.isArray(preview.teaser_ids) ? preview.teaser_ids.map(String) : []
    }
  }
  const ids = (prioritized || []).map(previewIdFor)
  return {
    readableIds: ids.slice(0, DIAGNOSIS_FREE_PREVIEW_LIMIT),
    teaserIds: ids.slice(DIAGNOSIS_FREE_PREVIEW_LIMIT, DIAGNOSIS_PAYWALL_LIST_CAP)
  }
}

/** The readable findings, then the teasers, each in id-list order. */
export function paywallPreviewOrder(preview, prioritized) {
  const { readableIds, teaserIds } = paywallPreview(preview, prioritized)
  const byId = new Map((prioritized || []).map((finding) => [previewIdFor(finding), finding]))
  return [...readableIds, ...teaserIds].map((id) => byId.get(id)).filter(Boolean)
}

export function findingIsWithinPaywallDisplay(lacksPlan, preview, prioritized, item) {
  if (!lacksPlan) return true
  const { readableIds, teaserIds } = paywallPreview(preview, prioritized)
  const id = previewIdFor(item)
  return readableIds.includes(id) || teaserIds.includes(id)
}

export function shouldBlurFinding(lacksPlan, preview, prioritized, item) {
  if (!lacksPlan) return false
  return paywallPreview(preview, prioritized).teaserIds.includes(previewIdFor(item))
}

export function isFindingDetailAccessible(lacksPlan, preview, prioritized, item) {
  if (!item) return false
  if (!lacksPlan) return true
  return paywallPreview(preview, prioritized).readableIds.includes(previewIdFor(item))
}

export function agentFindingPayload(finding) {
  const out = {}
  Object.entries(finding || {}).forEach(([key, value]) => {
    if (FINDING_WRAPPER_KEYS.includes(key)) return
    out[key] = value
  })
  return out
}

export function asList(value) {
  if (value == null || value === '') return []
  if (Array.isArray(value)) return value.filter((item) => item != null && item !== '')
  return [value]
}

export function isHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim())
}

export function humanizeKey(key) {
  return String(key || '').replace(/_/g, ' ')
}

export function yesNo(value) {
  if (value === true) return 'yes'
  if (value === false) return 'no'
  if (value == null || value === '') return null
  return String(value)
}

export function confidencePercent(finding) {
  const raw = finding?.confidence
  if (raw == null || raw === '') return null
  const num = Number(raw)
  if (Number.isNaN(num)) return null
  const pct = num <= 1 ? Math.round(num * 100) : Math.round(num)
  return Math.max(0, Math.min(100, pct))
}

export function bandMetaFor(code) {
  const key = String(code || '').toUpperCase()
  return GAP_BAND_META[key] || {
    title: humanizeKey(key) || 'Finding details',
    badge: 'FINDING-SPECIFIC',
    desc: 'Type-specific fields for this gap.'
  }
}

const HEADER_FIELD_KEYS = new Set([
  ...FINDING_WRAPPER_KEYS,
  'code',
  'title',
  'description',
  'why_it_exists',
  'source_of_issue',
  'supporting_evidence',
  'confidence',
  'root_cause_category',
  'severity',
  'fix_effort'
])

export function remainingTypeFields(finding) {
  return Object.entries(finding || {}).filter(([key, value]) => {
    if (HEADER_FIELD_KEYS.has(key)) return false
    if (value == null || value === '') return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  })
}

export function isRecommendedFieldKey(key) {
  return key === 'suggested_investigation' || /^recommended_/.test(key)
}

const SCHEMA_PAGE_SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 }

export function verdictMetaFor(verdict) {
  const key = String(verdict || '').trim().toLowerCase()
  return SCHEMA_VERDICT_META[key] || {
    label: (key ? humanizeKey(key) : 'unknown').toUpperCase(),
    tone: 'neutral',
    desc: ''
  }
}

export function schemaPageIssues(page) {
  return asList(page?.issues)
}

/**
 * Pages worth rendering, worst first. Clean pages are dropped; `not_assessed` ones are kept
 * even though they carry no issues, so an unreadable page is never mistaken for a healthy one.
 */
export function schemaPagesToShow(finding) {
  const pages = asList(finding?.page_diagnoses)
    .filter((page) => String(page?.verdict || '').trim().toLowerCase() !== 'conformant')
  return [...pages].sort((a, b) => {
    const sevDelta = (SCHEMA_PAGE_SEVERITY_RANK[String(a?.severity || '').toLowerCase()] ?? 99)
      - (SCHEMA_PAGE_SEVERITY_RANK[String(b?.severity || '').toLowerCase()] ?? 99)
    if (sevDelta !== 0) return sevDelta
    return schemaPageIssues(b).length - schemaPageIssues(a).length
  })
}

export function schemaPageStats(pages) {
  const list = pages || []
  const withIssues = list.filter((page) => schemaPageIssues(page).length > 0)
  return {
    pageCount: withIssues.length,
    issueCount: withIssues.reduce((total, page) => total + schemaPageIssues(page).length, 0),
    notAssessedCount: list.length - withIssues.length
  }
}

/** Rows show the path only — the full URL stays in the expanded body. */
export function pagePath(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''
  try {
    const parsed = new URL(raw)
    const path = `${parsed.pathname}${parsed.search}`
    return path === '/' ? '/ (homepage)' : path
  } catch {
    return raw
  }
}

/** issues[].evidence_refs mixes bare provenance tokens ('schema_profile') with URLs. */
export function splitEvidenceRefs(refs) {
  const tokens = []
  const urls = []
  asList(refs).forEach((ref) => {
    if (isHttpUrl(ref)) urls.push(ref)
    else tokens.push(String(ref))
  })
  return { tokens, urls }
}

export function brandInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}
